import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/router';
import { useEffect } from 'react';

interface AccountQuota {
  can_add: boolean | null | undefined;
  current_count: number;
  quota: number;
  remaining: number;
  subscription_tier: string;
  displayed_count?: number; // 화면에 실제로 표시되는 계정 수 (필터링된 결과)
  locked_count?: number; // Add the missing property for locked accounts count
}

/**
 * React Query hook to fetch and monitor a user's Instagram account connection quota
 * @returns Account quota information and loading state
 */
export function useAccountQuota() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Set up realtime subscription for account changes
  useEffect(() => {
    if (!session?.user?.id) return;

    // 기존 구독 중복 방지 위해 변수화
    const channelName = 'account_quota_changes_' + session.user.id.substring(0, 8);

    // Subscribe to saved_instagram_accounts table changes with debounce
    let debounceTimerId: ReturnType<typeof setTimeout>;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'saved_instagram_accounts',
          filter: `user_id=eq.${session.user.id}`
        },
        () => {
          // debounce the refresh to prevent multiple refreshes
          clearTimeout(debounceTimerId);
          debounceTimerId = setTimeout(() => {
            console.log('Account changes detected, refreshing quota data');
            queryClient.invalidateQueries({ queryKey: ['accountQuota'] });
          }, 5000); // 5초 debounce
        }
      )
      .subscribe();

    // Also subscribe to user_chat_credits table for quota changes
    const creditsChannel = supabase
      .channel('user_chat_credits_changes_' + session.user.id.substring(0, 8))
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_chat_credits',
          filter: `user_id=eq.${session.user.id}`
        },
        () => {
          // debounce the refresh to prevent multiple refreshes
          clearTimeout(debounceTimerId);
          debounceTimerId = setTimeout(() => {
            console.log('Credit/quota changes detected, refreshing quota data');
            queryClient.invalidateQueries({ queryKey: ['accountQuota'] });
          }, 5000); // 5초 debounce
        }
      )
      .subscribe();

    return () => {
      clearTimeout(debounceTimerId);
      channel.unsubscribe();
      creditsChannel.unsubscribe();
    };
  }, [session?.user?.id, queryClient]);

  return useQuery({
    queryKey: ['accountQuota'],
    queryFn: async (): Promise<AccountQuota> => {
      try {
        // 세션 확인
        if (!session?.user?.id) {
          // Return default values when user is not authenticated
          return {
            can_add: false,
            current_count: 0,
            quota: 2,
            remaining: 2,
            subscription_tier: 'free',
            displayed_count: 0
          };
        }

        // 1. 먼저 RPC 함수를 통해 쿼터 정보 가져오기 시도 - 함수명 수정
        const { data: quotaData, error: quotaError } = await supabase
          .rpc('get_account_quota_info');

        if (quotaError) {
          console.error('RPC 호출 오류:', quotaError);

          // RPC 실패 시 직접 쿼리로 폴백
          const { data: accountData, error: accountDataError } = await supabase
            .from('user_chat_credits')
            .select('account_quota')
            .eq('user_id', session.user.id)
            .single();

          if (accountDataError) {
            console.error('Credit 정보 조회 오류:', accountDataError);
            return {
              can_add: true, // 기본적으로 허용
              current_count: 0,
              quota: 2,
              remaining: 2,
              subscription_tier: 'free',
              displayed_count: 0
            };
          }

          // 2. 현재 계정 수 카운트
          const { count: currentCount, error: countError } = await supabase
            .from('saved_instagram_accounts')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', session.user.id)
            .is('deleted_at', null);

          if (countError) {
            console.error('계정 수 조회 오류:', countError);
            return {
              can_add: true,
              current_count: 0,
              quota: accountData.account_quota || 2,
              remaining: accountData.account_quota || 2,
              subscription_tier: 'free',
              displayed_count: 0
            };
          }

          const quota = accountData.account_quota || 2;
          const count = currentCount || 0;
          const remaining = quota === -1 ? -1 : Math.max(0, quota - count);

          return {
            can_add: quota === -1 || count < quota,
            current_count: count,
            quota: quota,
            remaining: remaining,
            subscription_tier: quota === -1 ? 'premium' : (quota === 10 ? 'basic' : 'free'),
            displayed_count: count
          };
        }

        // RPC 성공 케이스 - 결과 사용
        return {
          can_add: quotaData.can_add || false,
          current_count: quotaData.current_count || 0,
          quota: quotaData.quota || 2,
          remaining: quotaData.remaining || 0,
          subscription_tier: quotaData.subscription_tier || 'free',
          displayed_count: quotaData.current_count || 0
        };
      } catch (error) {
        console.error('계정 쿼터 확인 중 오류 발생:', error);
        // 기본값 반환
        return {
          can_add: true, // 기본적으로 허용
          current_count: 0,
          quota: 2,
          remaining: 2,
          subscription_tier: 'free',
          displayed_count: 0
        };
      }
    },

    // 설정
    staleTime: 1000 * 60 * 5, // 5분으로 연장 (기존 30초)
    refetchOnWindowFocus: false, // 자동 리프레시 끄기
    retry: 1
  });
}

/**
 * 구독 등급의 한글 이름을 반환하는 유틸리티 함수
 */
export function getSubscriptionTierName(tier: string): string {
  switch (tier) {
    case 'free':
      return '무료 계정';
    case 'basic':
      return '베이직 구독';
    case 'premium':
      return '프리미엄 구독';
    default:
      return '알 수 없음';
  }
}